import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { userChannelName } from '@/channels/utils/channelNames';
import MemberJoinedAlert from '@/components/MemberJoinedAlert';
import MemberJoinedAlertBar from '@/components/MemberJoinedAlertBar';
import { useChannelData } from '@/hooks/useChannel';
import useMemberJoinAlerts from '@/hooks/useMemberJoinAlerts';
import useInternalMixpanel from '@/hooks/useTracking/useInternalMixpanel';
import { IncomingAppMessage, ToastMessage } from '@/types/appMessage';
import { MemberJoinAlert } from '@/types/memberJoinAlert';
import Toast from '@/ui/components/Toast';
import { isMemberJoinedClearedMessage, isMemberJoinedMessage } from '@/utils/appMessageGuards';
import { healthieProviderAppointmentsLink } from '@/utils/healthieLinks';

import AppContext from './appContext';

interface NotificationContextInterface {
  memberJoinAlerts: MemberJoinAlert[];
  notifications: ToastMessage[];
}

const NotificationContext = createContext<NotificationContextInterface | undefined>(undefined);

export const NotificationProvider: React.FC = () => {
  const { currentUser, host } = useContext(AppContext);
  const { track } = useInternalMixpanel();

  const [notifications, setNotifications] = useState<ToastMessage[]>([]);

  const { activeAlert, alerts, clearAlert, maximizeAlert, minimizeAlert, receiveMessage } =
    useMemberJoinAlerts();

  const { channelData } = useChannelData<IncomingAppMessage>({
    channelId: userChannelName(currentUser?.id || ''),
    channelName: 'current-user-channel'
  });

  useEffect(() => {
    if (!channelData) {
      return;
    }

    if (isMemberJoinedMessage(channelData) || isMemberJoinedClearedMessage(channelData)) {
      receiveMessage(channelData);
      return;
    }

    setNotifications((prevNotifications) => {
      // Add the new message to the beginning of the array
      // If there is already a message with the same id, remove it first

      const updatedNotifications = prevNotifications.filter(
        (notification) => notification.collapse_id !== channelData.collapse_id
      );

      return [channelData as ToastMessage, ...updatedNotifications];
    });
  }, [channelData, receiveMessage]);

  const dismissMessage = (id: string) => {
    const updatedNotifications = notifications.filter((notification) => notification.id !== id);
    setNotifications(updatedNotifications);
  };

  // Counselors often work in Healthie rather than Dugout, so the alert offers
  // the appointment there. The anchor navigates; this only reports it, and the
  // alert stays up because they still have to join.
  const handleOpenHealthie = useCallback(
    (alert: MemberJoinAlert) => {
      track('Member Join Alert Opened In Healthie', {
        appointmentId: alert.data.appointment_id,
        secondsToOpen: Math.round((Date.now() - alert.receivedAt) / 1000),
        wasMinimized: alert.minimized
      });

      minimizeAlert(alert.collapseId);
    },
    [minimizeAlert, track]
  );

  const healthieUrl = (alert: MemberJoinAlert) =>
    alert.data.appointment_healthie_id
      ? healthieProviderAppointmentsLink(
          alert.data.appointment_healthie_id,
          alert.data.start_at_date,
          host
        )
      : null;

  return (
    <NotificationContext.Provider value={{ memberJoinAlerts: alerts, notifications }}>
      {!!notifications.length &&
        notifications.map((message, idx) => (
          <Toast
            key={`toast-message-${idx}-${message.id}}`}
            index={idx}
            message={message}
            dismissMessage={() => dismissMessage(message.id)}
          />
        ))}

      {activeAlert && (
        <MemberJoinedAlert
          alert={activeAlert}
          healthieUrl={healthieUrl(activeAlert)}
          onMinimize={minimizeAlert}
          onOpenHealthie={handleOpenHealthie}
          queuedCount={alerts.length - 1}
        />
      )}

      <MemberJoinedAlertBar
        alerts={alerts.filter((alert) => alert.collapseId !== activeAlert?.collapseId)}
        onDismiss={clearAlert}
        onMaximize={maximizeAlert}
      />
    </NotificationContext.Provider>
  );
};
